import {
  Outlet,
  useParams,
  useRouter,
  useRouterState,
} from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { Page } from '../../components/layout/page';
import { LabeledLink, PageHeader } from '../../components/layout/page-header';
import { TeamSelect } from '../../components/layout/TeamSelect';
import { useTeamTabs } from './team-tabs';

/**
 * Teams area layout: header with team switcher and the permission-gated tab
 * links. Tab gating lives in the route beforeLoad (tabGuard, SC-4792), so by
 * the time this wrapper mounts the active tab is visible — there is no
 * render-time redirect and no focus workaround to manage (SC-4791).
 */
export function TeamPageWrapper() {
  const { t } = useTranslation();
  const teamId = useParams({
    from: '/orgAuth/orgWithNav/$teamId/team',
    select: (params: { teamId: string }) => params.teamId,
  });

  const { navigate } = useRouter();
  const state = useRouterState();

  // Each tab is gated at the route's beforeLoad (SC-4792), so by the time
  // this wrapper mounts the active tab is visible — there is no
  // render-time redirect and no focus workaround to manage. The wrapper
  // renders the chrome and the visible tab links.
  const visibleTabs = useTeamTabs();

  return (
    <Page>
      <PageHeader
        title={t('teams')}
        rightSlot={
          <TeamSelect
            sx={{ mt: 0 }}
            teamId={teamId}
            onSelect={(newTeamId) => {
              const [, afterTeamId] = state.location.pathname.split(
                `/${teamId}/team`,
              );
              const newPath = `/${newTeamId}/team${afterTeamId || ''}`;
              navigate({
                to: newPath,
                search: state.location.search,
                replace: true,
                params: {},
              });
            }}
          />
        }
        navigationLinks={
          visibleTabs.map(
            (tab) =>
              ({
                to: tab.to,
                label: tab.label,
                params: { teamId },
              }) as LabeledLink,
          ) as LabeledLink[]
        }
      />
      <Outlet />
    </Page>
  );
}
